import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { projectKeys } from "hooks/useSidebarProjects";
import { useAuth } from "../contexts/AuthContext";
import { bugsnagClient } from "../bugsnag";
import { ANALYTICS_EVENTS, trackEvent } from "../utils/analytics";
import api from "utils/api";
import { Button } from "./common/Button";
import { AdminRolePill } from "./common/AdminRolePill";
import { TeamMemberSelectRow } from "./common/TeamMemberSelectRow";
import { ScrollFade } from "./common/ScrollFade";
import { Action, projectSubject, useAppAbility } from "common/access";
import { OrganizationType, OrganizationRole } from "common/constants";

const ProjectWizard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const ability = useAppAbility();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [organizationLoaded, setOrganizationLoaded] = useState(false);
  const [availableMembers, setAvailableMembers] = useState([]);
  const [teamAssignments, setTeamAssignments] = useState({});
  const [projectData, setProjectData] = useState({
    title: "",
    description: "",
    organizationId: null,
  });

  const isQuantitySurveyor =
    user?.organizationType === OrganizationType.QUANTITY_SURVEYOR;

  const canCreate = ability.can(
    Action.Create,
    projectSubject({ organizationId: projectData.organizationId }),
  );

  const roleOptions = useMemo(
    () => [
      { value: "member", label: t("organization_management.roles.member") },
      { value: "manager", label: t("organization_management.roles.manager") },
    ],
    [t],
  );

  const toggleTeamMember = useCallback((userId) => {
    setTeamAssignments((prev) => {
      const current = prev[userId];
      return {
        ...prev,
        [userId]: {
          role: current?.role ?? "member",
          selected: !current?.selected,
        },
      };
    });
  }, []);

  const setTeamMemberRole = useCallback((userId, role) => {
    setTeamAssignments((prev) => ({
      ...prev,
      [userId]: { selected: prev[userId]?.selected ?? true, role },
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!projectData.title.trim()) {
      setError(t("project_wizard.project_title_required"));
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const members = isQuantitySurveyor
        ? Object.entries(teamAssignments)
            .filter(([, assignment]) => assignment.selected)
            .map(([userId, assignment]) => ({
              userId: Number(userId),
              role: assignment.role,
            }))
        : undefined;

      const data = await api.createProject({
        name: projectData.title.trim(),
        description: projectData.description.trim() || undefined,
        organizationId: projectData.organizationId,
        members,
      });

      const projectId =
        data.project?._id || data.project?.id || data.id || data._id;

      if (!projectId) {
        throw new Error(t("project_wizard.no_id_returned"));
      }

      trackEvent(ANALYTICS_EVENTS.PROJECT_CREATED, {
        projectId: projectId,
        projectTitle: projectData.title,
        hasDescription: !!projectData.description.trim(),
        titleLength: projectData.title.length,
      });

      queryClient.invalidateQueries({ queryKey: projectKeys.all });

      navigate(
        isQuantitySurveyor
          ? `/project/${projectId}/smart-surveyor/upload`
          : `/project/${projectId}`,
      );
    } catch (err) {
      console.error("Project creation error:", err);
      setError(err.message || t("project_wizard.creation_failed"));
      bugsnagClient.notify(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProjectData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (error) {
      setError("");
    }
  };

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        const organization = await api.getMyOrganization();

        setProjectData((prev) => ({
          ...prev,
          organizationId: organization.organization.id,
        }));

        // QS admins assign team members at creation time. The creating admin is
        // excluded; other admins are shown as non-selectable rows.
        if (isQuantitySurveyor) {
          const members = (organization.organization.members || [])
            .filter(
              (member) =>
                member.user && member.user.id && member.user.id !== user?.id,
            )
            .map((member) => ({
              ...member.user,
              organizationRole: member.role,
            }));
          setAvailableMembers(members);
        }
      } catch (err) {
        console.error("Error fetching organization:", err);
        bugsnagClient.notify(err);
      } finally {
        setOrganizationLoaded(true);
      }
    };
    fetchOrganization();
  }, [isQuantitySurveyor, user?.id]);

  // Once the organization is known, redirect users who can't create projects.
  useEffect(() => {
    if (organizationLoaded && projectData.organizationId && !canCreate) {
      navigate("/", { replace: true });
    }
  }, [organizationLoaded, projectData.organizationId, canCreate, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {t("project_wizard.title")}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t("project_wizard.subtitle")}
          </p>
        </div>
        <div className="bg-white py-6 px-6 shadow-lg rounded-lg">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Project Title */}
            <div>
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("project_wizard.project_title_label")}{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={projectData.title}
                onChange={handleInputChange}
                placeholder={t("project_wizard.project_title_placeholder")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Project Description - Optional */}
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("project_wizard.description_label")}{" "}
                <span className="text-gray-400 text-xs">
                  {t("project_wizard.description_optional")}
                </span>
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                value={projectData.description}
                onChange={handleInputChange}
                placeholder={t("project_wizard.description_placeholder")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            {isQuantitySurveyor && availableMembers.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("project_wizard.team_label")}
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  {t("project_wizard.team_hint")}
                </p>
                <ScrollFade className="space-y-2 max-h-64 pr-1">
                  {availableMembers.map((member) => {
                    const fullName = [member.firstName, member.lastName]
                      .filter(Boolean)
                      .join(" ");
                    return (
                      <TeamMemberSelectRow
                        key={member.id}
                        name={fullName || member.email}
                        email={member.email}
                        isSelected={
                          teamAssignments[member.id]?.selected ?? false
                        }
                        role={teamAssignments[member.id]?.role ?? "member"}
                        roleOptions={roleOptions}
                        onToggle={() => toggleTeamMember(member.id)}
                        onRoleChange={(role) =>
                          setTeamMemberRole(member.id, role)
                        }
                        lockedRoleLabel={
                          member.organizationRole === OrganizationRole.ADMIN ? (
                            <AdminRolePill
                              label={t("organization_management.roles.admin")}
                            />
                          ) : undefined
                        }
                      />
                    );
                  })}
                </ScrollFade>
              </div>
            )}
            {/* Actions */}
            <div className="flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="default"
                size="lg"
                onClick={() => navigate("/")}
              >
                {t("project_wizard.cancel")}
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={isSubmitting || !projectData.title.trim()}
                startAdornment={
                  isSubmitting && (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  )
                }
              >
                {isSubmitting
                  ? t("project_wizard.creating")
                  : t("project_wizard.create_project")}
              </Button>
            </div>
          </form>
          {!isQuantitySurveyor && (
            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                {t("project_wizard.additional_details")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectWizard;
